import React, { useState, useEffect } from "react";
import { Button, Image, Modal } from "@mantine/core";
import apiService from "../../services/api";
import { LandingPageProps } from "../../types";

interface ContactFormData {
  username: string;
  subject: string;
  summary: string;
}

interface ContactMessage {
  type: string;
  text: string;
}

interface Feature {
  icon: string;
  title: string;
  description: string;
}

const LandingPage: React.FC<LandingPageProps> = ({
  onNavigateToClient,
  onNavigateToTrainer,
  onNavigateToAdmin,
}) => {
  const [showDisclosure, setShowDisclosure] = useState<boolean>(false);
  const [disclosureType, setDisclosureType] = useState<string>("");
  const [contactForm, setContactForm] = useState<ContactFormData>({
    username: "",
    subject: "",
    summary: "",
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [contactSubmitting, setContactSubmitting] = useState<boolean>(false);
  const [contactMessage, setContactMessage] = useState<ContactMessage>({
    type: "",
    text: "",
  });
  const [headerScrolled, setHeaderScrolled] = useState<boolean>(false);

  useEffect(() => {
    const handleScroll = () => {
      setHeaderScrolled(window.scrollY > window.innerHeight);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handlePortalAccess = (type: string) => {
    setDisclosureType(type);
    setShowDisclosure(true);
  };

  const handleContactChange = (field: string, value: string) => {
    setContactForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleContactSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedUsername = contactForm.username.trim();
    const trimmedSubject = contactForm.subject.trim();
    const trimmedSummary = contactForm.summary.trim();

    if (!trimmedUsername || !trimmedSubject || !trimmedSummary) {
      setContactMessage({
        type: "error",
        text: "Please complete all fields before submitting.",
      });
      return;
    }

    if (trimmedSubject.length > 200) {
      setContactMessage({
        type: "error",
        text: "Subject must be 200 characters or fewer.",
      });
      return;
    }

    setContactSubmitting(true);
    setContactMessage({ type: "", text: "" });

    try {
      await apiService.createSupportTicket({
        username: trimmedUsername,
        subject: trimmedSubject,
        summary: trimmedSummary,
      });

      setContactMessage({
        type: "success",
        text: "Thanks for reaching out! Your ticket has been submitted.",
      });
      setContactForm({ username: "", subject: "", summary: "" });
    } catch (error) {
      setContactMessage({
        type: "error",
        text: (error as Error).message || "We were unable to submit your ticket. Please try again.",
      });
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleAcceptDisclosure = () => {
    setShowDisclosure(false);
    switch (disclosureType) {
      case "client":
        onNavigateToClient();
        break;
      case "trainer":
        onNavigateToTrainer();
        break;
      case "admin":
        onNavigateToAdmin();
        break;
      default:
        break;
    }
  };

  const features: Feature[] = [
    {
      icon: "",
      title: "Expert Trainers",
      description: "Connect with experienced dog trainers who specialize in your specific needs.",
    },
    {
      icon: "",
      title: "Virtual Sessions",
      description:
        "Get professional training from the comfort of your home through secure video calls.",
    },
    {
      icon: "",
      title: "Easy Access",
      description: "Simple booking, session management, and progress tracking all in one platform.",
    },
    {
      icon: "",
      title: "Progress Tracking",
      description:
        "Monitor your dog's training progress with detailed summaries and actionable checklists.",
    },
    {
      icon: "",
      title: "Secure & Safe",
      description: "Your data and sessions are protected with enterprise-grade security measures.",
    },
    {
      icon: "",
      title: "Transparent Pricing",
      description: "Flexible pricing with transparent rates and no hidden fees.",
    },
  ];

  const disclosureText: Record<string, string> = {
    client: `By accessing the Client Portal, you agree to the following terms and conditions:

1. **Service Agreement**: You understand that Pheefee provides virtual dog training services through trainers.

2. **Payment Terms**: Sessions are billed at the trainer's published rates. The first 10 minutes are charged at a fixed rate, with additional time charged per minute.

3. **Session Recording**: Sessions may be recorded for quality assurance and training purposes. You will be notified before recording begins.

4. **Liability**: While our trainers are professionals, you understand that dog training involves inherent risks and agree to hold Pheefee harmless for any incidents.

5. **Data Privacy**: Your personal information and session data will be protected according to our privacy policy.

6. **Cancellation Policy**: Cancellations must be made at least 2 hours before your scheduled session to avoid charges.

Do you agree to these terms and wish to proceed to the Client Portal?`,

    trainer: `By accessing the Trainer Portal, you agree to the following terms and conditions:

1. **Independent Contractor Status**: You understand that you are an independent contractor, not an employee of Pheefee.

2. **Professional Standards**: You agree to maintain the highest professional standards and follow all applicable laws and regulations.

3. **Payment Terms**: You will receive payment on the first Monday of each month for completed sessions, minus platform fees.

4. **Background Check**: You consent to background checks and agree to provide all required documentation.

5. **Insurance Requirements**: You must maintain appropriate liability insurance and provide proof of coverage.

6. **Platform Rules**: You agree to follow all platform guidelines and maintain professional conduct during sessions.

7. **Data Security**: You will protect client information and maintain confidentiality at all times.

Do you agree to these terms and wish to proceed to the Trainer Portal?`,

    admin: `By accessing the Admin Portal, you agree to the following terms and conditions:

1. **Administrative Access**: You are authorized to access administrative functions and sensitive data.

2. **Data Security**: You will maintain the highest level of security and confidentiality for all platform data.

3. **Compliance**: You will ensure all operations comply with applicable laws and regulations.

4. **Audit Trail**: All administrative actions will be logged and monitored.

5. **Access Control**: You will not share administrative credentials or access with unauthorized personnel.

Do you agree to these terms and wish to proceed to the Admin Portal?`,
  };

  return (
    <div className="font-sans bg-background text-foreground flex flex-col scroll-smooth">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-20 py-5 transition-colors duration-300 ${headerScrolled ? "bg-[#1c1917]/85 backdrop-blur-sm" : "bg-transparent"}`}>
        <div className="max-w-[1200px] mx-auto px-5 flex items-center justify-between">
          <div className="flex items-center gap-4 cursor-pointer">
            <img
              src="/images/logo.png"
              alt="Pheefee Logo"
              className="w-[50px] h-[50px] rounded-xl object-contain"
            />
            <span className="text-2xl sm:text-3xl font-bold text-white">Pheefee</span>
          </div>
          {/* Desktop nav */}
          <nav className="hidden md:flex gap-8 items-center">
            <a
              href="#features"
              className="text-white/80 no-underline text-lg font-medium hover:text-white transition-all"
            >
              Features
            </a>
            <a
              href="#about"
              className="text-white/80 no-underline text-lg font-medium hover:text-white transition-all"
            >
              About
            </a>
            <a
              href="#contact"
              className="text-white/80 no-underline text-lg font-medium hover:text-white transition-all"
            >
              Contact
            </a>
            <span className="text-white/30">|</span>
            <button
              onClick={() => handlePortalAccess("client")}
              className="text-white/80 no-underline text-lg font-medium hover:text-white transition-all bg-transparent border-none cursor-pointer"
            >
              Client Login
            </button>
            <button
              onClick={() => handlePortalAccess("trainer")}
              className="text-white/80 no-underline text-lg font-medium hover:text-white transition-all bg-transparent border-none cursor-pointer"
            >
              Trainer Login
            </button>
          </nav>
          {/* Mobile hamburger */}
          <button
            className="md:hidden bg-transparent border-none cursor-pointer text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-7 h-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <nav className="md:hidden bg-black/80 backdrop-blur-sm mt-3 mx-5 rounded-xl p-5 flex flex-col gap-4">
            <a
              href="#features"
              onClick={() => setMobileMenuOpen(false)}
              className="text-white/80 no-underline text-lg font-medium hover:text-white transition-all"
            >
              Features
            </a>
            <a
              href="#about"
              onClick={() => setMobileMenuOpen(false)}
              className="text-white/80 no-underline text-lg font-medium hover:text-white transition-all"
            >
              About
            </a>
            <a
              href="#contact"
              onClick={() => setMobileMenuOpen(false)}
              className="text-white/80 no-underline text-lg font-medium hover:text-white transition-all"
            >
              Contact
            </a>
            <hr className="border-white/20 m-0" />
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                handlePortalAccess("client");
              }}
              className="text-white/80 no-underline text-lg font-medium hover:text-white transition-all bg-transparent border-none cursor-pointer text-left"
            >
              Client Login
            </button>
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                handlePortalAccess("trainer");
              }}
              className="text-white/80 no-underline text-lg font-medium hover:text-white transition-all bg-transparent border-none cursor-pointer text-left"
            >
              Trainer Login
            </button>
          </nav>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/images/landing.webp')" }}
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 flex flex-col items-center text-center px-6 mt-52">
          <span className="font-display text-2xl sm:text-4xl md:text-5xl text-white leading-relaxed drop-shadow">
            Professional dog training, on demand.
          </span>
          <span className="text-xl sm:text-3xl md:text-4xl text-white leading-relaxed drop-shadow">
            Connect with expert trainers from anywhere.
          </span>
        </div>
      </section>

      {/* About Section */}
      <section
        id="about"
        className="bg-background pt-6 sm:pt-8 md:pt-10 pb-10 sm:pb-16 md:pb-20 px-4 sm:px-5 border-t border-b border-border"
      >
        <div className="max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-10 items-stretch">
          <div className="text-left">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-5 text-primary">
              About Pheefee
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed mb-4">
              Pheefee pairs pet guardians with trainers who are passionate about helping dogs
              thrive. We blend proven training methodologies with real-time virtual coaching so you
              can build consistent habits that fit your lifestyle.
            </p>
            <p className="text-base text-muted-foreground leading-relaxed mb-4">
              From puppy socialization to advanced behavior modification, our trainers collaborate
              with you to create actionable plans, track progress, and celebrate every milestone
              along the journey. Training should feel empowering, and we make sure you have the
              tools to succeed long after each session ends.
            </p>
            <div className="grid gap-4 mt-5">
              <div className="bg-accent-subtle border border-accent/25 rounded-xl px-5 py-4 text-accent text-sm leading-relaxed">
                Trainers are screened for specialty experience
              </div>
              <div className="bg-accent-subtle border border-accent/25 rounded-xl px-5 py-4 text-accent text-sm leading-relaxed">
                Structured session recaps and personalized task lists
              </div>
              <div className="bg-accent-subtle border border-accent/25 rounded-xl px-5 py-4 text-accent text-sm leading-relaxed">
                Secure video platform with integrated payments and scheduling
              </div>
            </div>
          </div>
          <Image
            src="/images/dog-trainer-1.jpg"
            alt="man holding a leashed training dog"
            className="rounded-2xl h-full w-full object-cover"
          />
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="bg-muted py-10 sm:py-16 md:py-20 px-4 sm:px-5 mt-0">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-12 text-primary">
            Why Choose Pheefee?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-card rounded-2xl p-8 text-center border border-border shadow-lg hover:shadow-xl transition-shadow"
              >
                <span className="text-5xl mb-5 block text-primary">{feature.icon}</span>
                <h3 className="font-display text-xl font-semibold mb-4 text-card-foreground">
                  {feature.title}
                </h3>
                <p className="text-base text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className="bg-background py-10 sm:py-16 md:py-20 px-4 sm:px-5 border-t border-border"
      >
        <div className="max-w-[900px] mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-primary">
            Contact Us
          </h2>
          <p className="text-lg text-muted-foreground mb-9 leading-relaxed">
            Have a question, feature request, or need help with your account? Send us a ticket and
            our team will get back to you within one business day.
          </p>
          <form
            className="bg-card rounded-2xl p-5 sm:p-7 md:p-9 border border-border shadow-[0_10px_30px_rgba(230,126,34,0.12)] text-left"
            onSubmit={handleContactSubmit}
          >
            <div className="mb-5">
              <label className="block text-card-foreground mb-2 font-semibold">Username</label>
              <input
                className="w-full p-3 rounded-xl border border-border bg-input text-foreground text-base focus:border-primary transition"
                type="text"
                value={contactForm.username}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleContactChange("username", e.target.value)
                }
                placeholder="Enter the username tied to your account"
              />
            </div>
            <div className="mb-5">
              <label className="block text-card-foreground mb-2 font-semibold">Subject</label>
              <input
                className="w-full p-3 rounded-xl border border-border bg-input text-foreground text-base focus:border-primary transition"
                type="text"
                value={contactForm.subject}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleContactChange("subject", e.target.value)
                }
                placeholder="How can we help?"
                maxLength={200}
              />
            </div>
            <div>
              <label className="block text-card-foreground mb-2 font-semibold">Issue Summary</label>
              <textarea
                className="w-full min-h-[160px] p-3.5 rounded-xl border border-border bg-input text-foreground text-base resize-y mb-6 leading-relaxed focus:border-primary transition"
                value={contactForm.summary}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  handleContactChange("summary", e.target.value)
                }
                placeholder="Share details about the issue or question you're experiencing..."
              />
            </div>
            <Button type="submit" fullWidth loading={contactSubmitting} radius="xl" size="lg">
              Submit Support Ticket
            </Button>
            {contactMessage.text && (
              <div
                className={`mt-4 text-sm font-medium ${contactMessage.type === "success" ? "text-success" : "text-destructive"}`}
              >
                {contactMessage.text}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-muted border-t border-border py-10 px-5 text-center text-muted-foreground">
        <p>&copy; 2026 Pheefee. All rights reserved. | Privacy Policy | Terms of Service</p>
      </footer>

      {/* Disclosure Modal */}
      <Modal
        opened={showDisclosure}
        onClose={() => setShowDisclosure(false)}
        title={
          disclosureType === "client"
            ? "Client Portal Agreement"
            : disclosureType === "trainer"
              ? "Trainer Portal Agreement"
              : "Admin Portal Agreement"
        }
        centered
        radius="lg"
        size="lg"
      >
        <div className="text-base leading-relaxed mb-8 text-muted-foreground whitespace-pre-line">
          {disclosureText[disclosureType]}
        </div>
        <div className="flex gap-4 justify-center">
          <Button onClick={handleAcceptDisclosure} radius="md" size="lg">
            I Agree - Continue
          </Button>
          <Button
            onClick={() => setShowDisclosure(false)}
            variant="outline"
            color="red"
            radius="md"
            size="lg"
          >
            Cancel
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default LandingPage;
